import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import StatsOverview from './components/StatsOverview/StatsOverview';
import UsersList from './components/UsersList/UsersList';
import AnalyticsDashboard from './components/AnalyticsDashboard/AnalyticsDashboard';
import { fetchAdminStats, UserStat } from '../../services/adminService';

const AdminDashboard: React.FC = () => {
    const [users, setUsers] = useState<UserStat[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState<'users' | 'analytics'>('users');

    // Filtros
    const [filterPlan, setFilterPlan] = useState<string>('all');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [filterWhatsApp, setFilterWhatsApp] = useState<string>('all');

    const loadAdminStats = async () => {
        setIsLoading(true);
        try {
            const data = await fetchAdminStats();
            setUsers(data);
        } catch (error) {
            console.error('Error loading admin stats:', error);
            toast.error('Erro ao carregar estatísticas');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadAdminStats();
    }, []);

    // Filtrar usuários por nome, email e filtros
    const filteredUsers = users.filter(user => {
        // Filtro de busca (nome ou email)
        const matchesSearch = user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase());

        // Filtro de plano
        const matchesPlan = filterPlan === 'all' || user.subscription_status === filterPlan;

        // Filtro de status (ativo = pro ou basic)
        const matchesStatus = filterStatus === 'all' ||
            (filterStatus === 'active' && (user.subscription_status === 'pro' || user.subscription_status === 'basic')) ||
            (filterStatus === 'inactive' && (user.subscription_status === 'free' || user.subscription_status === 'inactive'));

        // Filtro de WhatsApp
        const matchesWhatsApp = filterWhatsApp === 'all' ||
            (filterWhatsApp === 'connected' && user.whatsapp_connected) ||
            (filterWhatsApp === 'disconnected' && !user.whatsapp_connected);

        return matchesSearch && matchesPlan && matchesStatus && matchesWhatsApp;
    });

    // Calcular estatísticas globais
    const globalStats = {
        totalUsers: users.length,
        activeUsers: users.filter(u => u.subscription_status === 'pro' || u.subscription_status === 'basic').length,
        totalLeads: users.reduce((sum, u) => sum + u.total_leads, 0),
        totalProspectedLeads: users.reduce((sum, u) => sum + u.prospected_leads, 0),
        totalSearches: users.reduce((sum, u) => sum + u.total_searches, 0),
        totalTokens: users.reduce((sum, u) => sum + u.total_tokens_used, 0),
        totalCost: users.reduce((sum, u) => sum + u.total_cost, 0),
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <span className="material-symbols-outlined text-4xl text-primary animate-spin">sync</span>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8"
        >
            {/* Header with Tabs */}
            <div className="mb-6 sm:mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold text-text-dark dark:text-white mb-4">
                    Painel de Administrador
                </h1>

                {/* Tab Navigation */}
                <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
                    <button
                        onClick={() => setActiveTab('users')}
                        className={`px-4 py-2 font-medium transition-all ${activeTab === 'users'
                                ? 'text-primary border-b-2 border-primary'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                            }`}
                    >
                        <span className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-xl">group</span>
                            Gerenciar Usuários
                        </span>
                    </button>
                    <button
                        onClick={() => setActiveTab('analytics')}
                        className={`px-4 py-2 font-medium transition-all ${activeTab === 'analytics'
                                ? 'text-primary border-b-2 border-primary'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                            }`}
                    >
                        <span className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-xl">analytics</span>
                            Analytics & Uso
                        </span>
                    </button>
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'users' ? (
                <>
                    {/* Stats Overview */}
                    <StatsOverview stats={globalStats} />

                    {/* Aviso informativo de controle de acesso rápido */}
                    <div className="my-4 bg-amber-500/10 dark:bg-amber-900/20 border border-amber-500/30 rounded-xl p-3 sm:p-4 flex items-center justify-between text-xs sm:text-sm text-amber-800 dark:text-amber-200">
                        <div className="flex items-center gap-2.5">
                            <span className="material-symbols-outlined text-amber-600 dark:text-amber-400 text-xl flex-shrink-0">switch_account</span>
                            <span>
                                <strong>Acesso Direto à Conta:</strong> Você pode entrar na conta de qualquer usuário clicando no botão <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-700 dark:text-amber-300 font-bold mx-1"><span className="material-symbols-outlined text-xs mr-0.5">switch_account</span> Acessar</span> na tabela abaixo para navegar e realizar alterações na visão do cliente.
                            </span>
                        </div>
                    </div>

                    {/* Filtros e Busca */}
                    <div className="mb-4 sm:mb-6 space-y-3">
                        {/* Barra de Busca */}
                        <div className="relative">
                            <span className="material-symbols-outlined absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-text-dark/50 dark:text-gray-500">
                                search
                            </span>
                            <input
                                type="text"
                                placeholder="Buscar por nome ou email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 rounded-lg bg-white dark:bg-gray-800 text-text-dark dark:text-white border-2 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>

                        {/* Filtros */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {/* Filtro de Plano */}
                            <div>
                                <label className="block text-xs font-medium text-text-dark/70 dark:text-gray-400 mb-1">
                                    Plano
                                </label>
                                <select
                                    value={filterPlan}
                                    onChange={(e) => setFilterPlan(e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-800 text-text-dark dark:text-white border-2 border-gray-200 dark:border-gray-700 focus:outline-non focus:ring-2 focus:ring-primary focus:border-transparent"
                                >
                                    <option value="all">Todos os planos</option>
                                    <option value="free">Free</option>
                                    <option value="basic">Basic</option>
                                    <option value="pro">Pro</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>

                            {/* Filtro de Status */}
                            <div>
                                <label className="block text-xs font-medium text-text-dark/70 dark:text-gray-400 mb-1">
                                    Status
                                </label>
                                <select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-800 text-text-dark dark:text-white border-2 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                >
                                    <option value="all">Todos</option>
                                    <option value="active">Ativos (Pro/Basic)</option>
                                    <option value="inactive">Inativos (Free/Inactive)</option>
                                </select>
                            </div>

                            {/* Filtro de WhatsApp */}
                            <div>
                                <label className="block text-xs font-medium text-text-dark/70 dark:text-gray-400 mb-1">
                                    WhatsApp
                                </label>
                                <select
                                    value={filterWhatsApp}
                                    onChange={(e) => setFilterWhatsApp(e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-800 text-text-dark dark:text-white border-2 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                >
                                    <option value="all">Todos</option>
                                    <option value="connected">Conectado</option>
                                    <option value="disconnected">Desconectado</option>
                                </select>
                            </div>
                        </div>

                        {/* Contador de resultados */}
                        <div className="text-sm text-text-dark/70 dark:text-gray-400">
                            Mostrando <span className="font-semibold text-primary">{filteredUsers.length}</span> de{' '}
                            <span className="font-semibold">{users.length}</span> usuários
                        </div>
                    </div>

                    {/* Users List */}
                    <UsersList users={filteredUsers} onRefresh={loadAdminStats} />
                </>
            ) : (
                <AnalyticsDashboard users={users} />
            )}
        </motion.div>
    );
};

export default AdminDashboard;
